import InputExame from "./InputExame";
import ImportarExamesLabModal from "./ImportarExamesLabModal";
import {
  Bot,
  FlaskConical,
  ClipboardList,
  AlertTriangle,
  Lightbulb,
  Clock,
  CircleDot,
  Loader,
  Plus,
  Trash2,
  Pencil,
  FileUp,
} from "lucide-react";
import { useExames } from "../context/ExamesContext";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  getDataHoje,
  formatarDataBR,
  formatarDataISO,
} from "../../../utils/dates";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import Button from "../../../shared/components/ui/Button";
import { calcularMELD } from "../../../utils/meld";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, msgDataFutura } from "../../../shared/utils/dataNaoFutura";
import TextoAnaliseMarkdown from "../../../shared/components/TextoAnaliseMarkdown";
import { Paciente } from "../../../types";
// ADICIONAR IMPORTS NO TOPO
import { useAnaliseContextual } from "../../ia-analise/hooks/useAnaliseContextual";
import { AnaliseIA } from "../../ia-analise/types";
import SelosStatus from "../../ia-analise/components/SelosStatus";
import { useAuth } from "../../auth/context/AuthContext";
import {
  podeEscreverClinico,
  podeImportarExamesPDF,
  ehAdministrador,
  ehMedico,
} from "../../../shared/utils/permissoesCategoria";

interface ExameLab {
  id: string;
  data: string;
  editavel: boolean;
  meld?: number;
  childPugh?: string;
  encefalopatia?: string;
  ascite?: string;

  // ✅ CAMPOS PÓS-TX (logo após data)
  po?: number; // Dias pós-operatório (calculado automaticamente)
  temperatura?: number; // Temperatura corporal
  peso?: number; // Peso em kg
  dialise?: boolean; // Sim/Não
  transfusao?: string; // Campo livre
  diurese?: number; // Volume de diurese

  // HEMOGRAMA
  hemoglobina?: number;
  hematocrito?: number;
  leucocitos?: number;
  neutrofilos?: number;
  eosinofilos?: number;
  linfocitos?: number;
  bastonetes?: number; // B342 (AI_150)
  plaquetas?: number;

  // COAGULAÇÃO
  tap?: number;
  inr?: number;
  ttpa?: number;
  fibrinogenio?: number; // ✅ NOVO
  fatorV?: number; // ✅ NOVO

  // FUNÇÃO HEPÁTICA
  bilirrubinaTotal?: number;
  bilirrubinaDireta?: number;
  bilirrubinaIndireta?: number;
  tgo?: number;
  tgp?: number;
  fosfataseAlcalina?: number;
  ggt?: number;
  albumina?: number;
  proteinasTotais?: number; // B342 (AI_150)
  globulina?: number; // B342 (AI_150)

  // FUNÇÃO RENAL
  creatinina?: number;
  ureia?: number;

  // ELETRÓLITOS
  sodio?: number;
  potassio?: number;
  magnesio?: number;
  calcio?: number;
  calcioIonico?: number; // B241 fase 2 (AI_109)
  fosforo?: number; // B342 (AI_150) — FOSF do folhão (≠ fosfatase alcalina)
  cloro?: number; // B342 (AI_150)

  // PÂNCREAS (B241 fase 2 — AI_109)
  amilase?: number;
  lipase?: number;

  // LIPÍDIOS
  colesterolTotal?: number;
  ldl?: number;
  hdl?: number;
  vldl?: number;
  triglicerides?: number;

  // TIREOIDE
  tsh?: number;
  t4Livre?: number;
  t3Total?: number; // B241 fase 2 (AI_109)

  // METABOLISMO (B241 fase 2 — AI_109)
  acidoUrico?: number;

  // ✅ NOVOS CAMPOS (metabolismo/infecção)
  glicemia?: number;
  hemoglobinaGlicada?: number;
  frutosamina?: number;
  pcr?: number; // Proteína C Reativa
  procalcitonina?: number; // ✅ NOVO
  lactato?: number; // ✅ NOVO
  ldh?: number; // B342 (AI_150)
  cpk?: number; // B342 (AI_150)
  ckMb?: number; // B342 (AI_150)
  troponina?: number; // B342 (AI_150)

  // ✅ GASOMETRIA COMPLETA (7 campos)
  gasometriaTipo?: "arterial" | "venosa"; // ✅ NOVO
  gasometriaPh?: number; // ✅ NOVO
  gasometriaPco2?: number; // ✅ NOVO
  gasometriaPo2?: number; // ✅ NOVO
  gasometriaHco3?: number; // ✅ NOVO
  gasometriaBe?: number; // ✅ NOVO (Base Excess)
  gasometriaSaturacao?: number; // ✅ NOVO (Saturação O2)

  // IMUNOSSUPRESSÃO (campos pós-tx)
  solumedrol?: number;
  prednisona?: number;
  tacrolimus?: number;
  nivelTacrolimus?: number;
  ciclosporina?: number;
  nivelCiclosporina?: number;
  mmf?: number;
  mms?: number;
  everolimus?: number;
  nivelEverolimus?: number;
  sirolimus?: number;
  nivelSirolimus?: number;
  azatioprina?: number;
  thymoglobulina?: number;
  registradoPor?: string;
  registradoPorNome?: string;
  criadoEm?: string;
}

// 🐛 B338 (AI_149) — campos de que os SCORES derivam. Ao editar/apagar um deles e
// o score deixar de ser calculável, o valor gravado vira lixo (foi feito com o
// dado antigo) e precisa ser apagado junto. Fora desta lista, o score não é tocado.
const COMPONENTES_MELD = new Set([
  "creatinina",
  "bilirrubinaTotal",
  "inr",
  "sodio",
  "albumina",
]);
const COMPONENTES_CHILD = new Set([
  "bilirrubinaTotal",
  "albumina",
  "inr",
  "ascite",
  "encefalopatia",
]);

const valoresNormais = {
  // Campos existentes...
  hemoglobina: { min: 12, max: 17, unidade: "g/dL" },
  hematocrito: { min: 36, max: 54, unidade: "%" },
  leucocitos: { min: 4000, max: 11000, unidade: "/mm³" },
  // B241 (AI_108): diferencial em valor ABSOLUTO (/mm³), não %. Faixas de
  // referência padrão de adulto — confirmar/ajustar clinicamente se desejar.
  neutrofilos: { min: 1800, max: 7700, unidade: "/mm³" },
  eosinofilos: { min: 0, max: 500, unidade: "/mm³" },
  linfocitos: { min: 1000, max: 4000, unidade: "/mm³" },
  plaquetas: { min: 150000, max: 450000, unidade: "/mm³" },
  tap: { min: 70, max: 100, unidade: "%" },
  inr: { min: 0.8, max: 1.2, unidade: "" },
  ttpa: { min: 25, max: 35, unidade: "seg" },

  // ✅ NOVOS - COAGULAÇÃO
  fibrinogenio: { min: 200, max: 400, unidade: "mg/dL" },
  fatorV: { min: 70, max: 120, unidade: "%" },

  bilirrubinaTotal: { min: 0.3, max: 1.2, unidade: "mg/dL" },
  bilirrubinaDireta: { min: 0, max: 0.3, unidade: "mg/dL" },
  bilirrubinaIndireta: { min: 0.1, max: 0.9, unidade: "mg/dL" },
  tgo: { min: 0, max: 40, unidade: "U/L" },
  tgp: { min: 0, max: 41, unidade: "U/L" },
  fosfataseAlcalina: { min: 30, max: 120, unidade: "U/L" },
  ggt: { min: 0, max: 60, unidade: "U/L" },
  albumina: { min: 3.5, max: 5.0, unidade: "g/dL" },
  creatinina: { min: 0.6, max: 1.2, unidade: "mg/dL" },
  ureia: { min: 15, max: 40, unidade: "mg/dL" },
  sodio: { min: 135, max: 145, unidade: "mEq/L" },
  potassio: { min: 3.5, max: 5.0, unidade: "mEq/L" },
  magnesio: { min: 1.7, max: 2.2, unidade: "mg/dL" },
  calcio: { min: 8.5, max: 10.5, unidade: "mg/dL" },
  colesterolTotal: { min: 0, max: 200, unidade: "mg/dL" },
  ldl: { min: 0, max: 100, unidade: "mg/dL" },
  hdl: { min: 40, max: 999, unidade: "mg/dL" },
  vldl: { min: 0, max: 30, unidade: "mg/dL" },
  triglicerides: { min: 0, max: 150, unidade: "mg/dL" },
  tsh: { min: 0.4, max: 4.0, unidade: "µUI/mL" },
  t4Livre: { min: 0.8, max: 1.8, unidade: "ng/dL" },

  // ✅ NOVOS - PÓS-TX (após data)
  temperatura: { min: 36.0, max: 37.5, unidade: "°C" },
  peso: { min: 40, max: 150, unidade: "kg" },
  diurese: { min: 800, max: 3000, unidade: "mL/24h" },

  // ✅ NOVOS - METABOLISMO/INFECÇÃO
  glicemia: { min: 70, max: 100, unidade: "mg/dL" },
  hemoglobinaGlicada: { min: 0, max: 5.7, unidade: "%" },
  frutosamina: { min: 205, max: 285, unidade: "µmol/L" },
  pcr: { min: 0, max: 3, unidade: "mg/L" },
  procalcitonina: { min: 0, max: 0.5, unidade: "ng/mL" },
  lactato: { min: 0.5, max: 2.2, unidade: "mmol/L" },

  // ✅ NOVOS - GASOMETRIA
  gasometriaPh: { min: 7.35, max: 7.45, unidade: "" },
  gasometriaPco2: { min: 35, max: 45, unidade: "mmHg" },
  gasometriaPo2: { min: 80, max: 100, unidade: "mmHg" },
  gasometriaHco3: { min: 22, max: 26, unidade: "mEq/L" },
  gasometriaBe: { min: -2, max: 2, unidade: "mEq/L" },
  gasometriaSaturacao: { min: 95, max: 100, unidade: "%" },

  // ✅ B241 fase 2 (AI_109) — pâncreas, eletrólito iônico, metabolismo, tireoide
  amilase: { min: 28, max: 100, unidade: "U/L" },
  lipase: { min: 13, max: 60, unidade: "U/L" },
  acidoUrico: { min: 3.5, max: 7.2, unidade: "mg/dL" },
  calcioIonico: { min: 1.1, max: 1.35, unidade: "mmol/L" },
  t3Total: { min: 80, max: 200, unidade: "ng/dL" },

  // ✅ B342 (AI_150) — campos do folhão do pós-operatório.
  // Fósforo: faixa confirmada por Leonardo (adulto).
  fosforo: { min: 2.5, max: 4.5, unidade: "mg/dL" },
  cloro: { min: 98, max: 107, unidade: "mEq/L" },
  bastonetes: { min: 0, max: 700, unidade: "/mm³" },
  ldh: { min: 140, max: 280, unidade: "U/L" },
  proteinasTotais: { min: 6.0, max: 8.0, unidade: "g/dL" },
  globulina: { min: 2.0, max: 3.5, unidade: "g/dL" },
  cpk: { min: 30, max: 200, unidade: "U/L" },
  ckMb: { min: 0, max: 5, unidade: "ng/mL" },
  troponina: { min: 0, max: 0.04, unidade: "ng/mL" },
};

const verificarValor = (campo: keyof typeof valoresNormais, valor?: number) => {
  if (valor === undefined || valor === null) return null;
  const ref = valoresNormais[campo];
  if (!ref) return null;

  if (campo === "hemoglobina" && valor > ref.max) {
    return { status: "normal", cor: "#10b981", indicador: "✓" };
  }
  if (campo === "albumina" && valor > ref.max) {
    return { status: "normal", cor: "#10b981", indicador: "✓" };
  }
  if (campo === "creatinina" && valor < ref.min) {
    return { status: "normal", cor: "#10b981", indicador: "✓" };
  }

  if (valor < ref.min)
    return { status: "baixo", cor: "#ef4444", indicador: "↓" };
  if (valor > ref.max)
    return { status: "alto", cor: "#ef4444", indicador: "↑" };
  return { status: "normal", cor: "#10b981", indicador: "✓" };
};

// ========================================
// 🎯 HOOK PARA NAVEGAÇÃO COM TAB/ENTER - VERSÃO SIMPLIFICADA
// ========================================

const useTableNavigation = () => {
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    currentKey: string,
    allKeys: string[],
    onNavigate: (key: string) => void
  ) => {
    // TAB (sem shift): próximo campo
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      const currentIndex = allKeys.indexOf(currentKey);
      const nextIndex = (currentIndex + 1) % allKeys.length;
      onNavigate(allKeys[nextIndex]);
    }

    // SHIFT + TAB: campo anterior
    if (e.key === "Tab" && e.shiftKey) {
      e.preventDefault();
      const currentIndex = allKeys.indexOf(currentKey);
      const prevIndex =
        currentIndex - 1 < 0 ? allKeys.length - 1 : currentIndex - 1;
      onNavigate(allKeys[prevIndex]);
    }

    // ENTER: próximo campo (igual TAB)
    if (e.key === "Enter") {
      e.preventDefault();
      const currentIndex = allKeys.indexOf(currentKey);
      const nextIndex = (currentIndex + 1) % allKeys.length;
      onNavigate(allKeys[nextIndex]);
    }
  };

  return { handleKeyDown };
};

interface ExamesLaboratoriaisProps {
  paciente: Paciente;
  // B249/AI_111: força o modo PRÉ-Tx (editar labs pré) mesmo num paciente já
  // transplantado — usado na sub-aba "Exames Laboratoriais" do Pré-Transplante.
  modoPreForcado?: boolean;
}
export default function ExamesLaboratoriais({
  paciente,
  modoPreForcado = false,
}: ExamesLaboratoriaisProps) {
  const {
    listarExamesPorPaciente,
    listarExamesPreTx,
    listarExamesPosTx,
    adicionarExame,
    editarExame,
    removerExame,
    marcarExamesComoPreTx,
    limparExamesFantasmas,
  } = useExames();

  const sexoFeminino = paciente?.sexo === "F";
  const isPosTx = modoPreForcado
    ? false
    : !!(paciente?.inscricaoTx?.dataTransplante || paciente?.dataTransplante);
  const isFigado = paciente?.tipoAtendimento === "figado";
  // ========================================
  // 🤖 ANÁLISE IA - ESTADOS E HOOK
  // ========================================
  const {
    loading: loadingAnalise,
    erro: erroAnalise,
    analisarExamesLab,
  } = useAnaliseContextual();
  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): nome legado - hoje significa "somente leitura".
  // Cobre convidado E observador (espelho de canWriteClinical das rules).
  const isConvidado = !podeEscreverClinico(usuarioLogado);
  // 🔐 B323 (AI_145): admin mantém editar/excluir mesmo após 24h (escape de
  // correção de erro antigo — caso B296). A equipe trava; só o admin passa.
  const ehAdmin = ehAdministrador(usuarioLogado);
  // §6.3.2/B88 (AI_145): GERAÇÃO de IA é ato médico. A leitura da última análise
  // salva segue liberada à equipe (!isConvidado); só o GERAR exige médico/admin.
  const podeGerarIA = ehMedico(usuarioLogado);
  const [analiseAtual, setAnaliseAtual] = useState<AnaliseIA | null>(null);
  const [mostrarAnalise, setMostrarAnalise] = useState(false);
  // 🧪 B323-FU (AI_145): última análise lab persistida (durável) OU a recém-gerada
  // nesta sessão — alimenta o botão "Ver última análise" (padrão das demais abas).
  const { atualizarPaciente } = usePacientes();
  const [ultimaAnaliseLabLocal, setUltimaAnaliseLabLocal] =
    useState<AnaliseIA | null>(null);
  const ultimaAnaliseLabDisponivel: AnaliseIA | null =
    (paciente.ultimaAnaliseLab?.analise as AnaliseIA | undefined) ||
    ultimaAnaliseLabLocal ||
    null;
  // B241 (AI_108): modal de importação de exames laboratoriais por PDF
  const [mostrarImportar, setMostrarImportar] = useState(false);
  // Gatilho explícito de re-sincronização após importar (força o useEffect de
  // sync pós-Tx a re-rodar mesmo que a identidade de listarExamesPosTx não mude
  // no instante exato — evita "só aparece após recarregar a página").
  const [refreshImport, setRefreshImport] = useState(0);

  // Função para analisar todos os exames (analisa o mais recente com contexto de todos)
  const handleAnalisarFolhao = async () => {
    // Precisa ter pelo menos 1 exame
    if (exames.length === 0) {
      alert("⚠️ Nenhum exame disponível para análise");
      return;
    }

    try {
      setMostrarAnalise(false);
      setAnaliseAtual(null);

      // Analisa o exame mais recente (primeiro da lista ordenada)
      const exameRecente = exames[0];

      // [B196] Logs de conteúdo clínico removidos (LGPD) — não logar campos do exame.
      const resultado = await analisarExamesLab(paciente, exameRecente.id);

      if (resultado) {
        setAnaliseAtual(resultado);
        setMostrarAnalise(true);
        // 💾 B323-FU (AI_145): persiste a última análise lab no paciente (durável),
        // para reabrir depois com "Ver última análise". Soft-fail: se a gravação
        // falhar, a análise ainda é exibida nesta sessão (estado local).
        setUltimaAnaliseLabLocal(resultado);
        try {
          await atualizarPaciente(paciente.id, {
            ultimaAnaliseLab: {
              analise: resultado,
              geradaEm: new Date().toISOString(),
              geradaPor: usuarioLogado?.id || "",
              geradaPorNome: usuarioLogado?.nome || "",
            },
          });
        } catch (e) {
          console.warn("⚠️ Não foi possível salvar a última análise lab:", e);
        }

        // Scroll suave até a análise
        setTimeout(() => {
          const elementoAnalise = document.getElementById("analise-ia-folhao");
          if (elementoAnalise) {
            elementoAnalise.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Erro ao analisar folhão:", error);
      alert("❌ Erro ao analisar exames laboratoriais");
    }
  };

  // 🧪 B323-FU (AI_145): reabre a última análise lab salva (durável) no modal —
  // mesmo padrão do "Ver última análise" de marcadores/sorologias/evoluções.
  const verUltimaAnaliseLab = () => {
    const ua = ultimaAnaliseLabDisponivel;
    if (!ua) return;
    setAnaliseAtual(ua);
    setMostrarAnalise(true);
    setTimeout(() => {
      document
        .getElementById("analise-ia-folhao")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // ========================================
  // 🎯 SISTEMA DE NAVEGAÇÃO - VERSÃO SIMPLIFICADA
  // ========================================
  const inputRefs = useRef<{
    [key: string]: HTMLInputElement | HTMLSelectElement | null;
  }>({});

  // Função que foca em um input específico pela chave
  const focusInputByKey = useCallback((key: string) => {
    // Buscar diretamente no DOM pelo data-key
    // Isso funciona mesmo após re-render porque busca o elemento atual
    setTimeout(() => {
      const input = document.querySelector(
        `input[data-key="${key}"], select[data-key="${key}"]`
      ) as HTMLInputElement | HTMLSelectElement;

      if (!input) return;

      input.focus();

      // Selecionar conteúdo
      if (
        input.type === "text" ||
        input.type === "number" ||
        input.type === "date"
      ) {
        if (
          "select" in input &&
          typeof input.select === "function" &&
          input.tagName === "INPUT"
        ) {
          (input as HTMLInputElement).select();
        }
      }
    }, 120);
  }, []);
  // Ativa o hook de navegação
  const { handleKeyDown } = useTableNavigation();

  // ✅ FUNÇÃO PARA CALCULAR CHILD-PUGH
  const calcularChildPugh = (
    exame: any
  ): { score: number; classe: string } | null => {
    const bilirrubina = exame.bilirrubinaTotal;
    const albumina = exame.albumina;
    const inr = exame.inr;
    const ascite = exame.ascite;
    const encefalopatia = exame.encefalopatia;

    if (!bilirrubina || !albumina || !inr || !ascite || !encefalopatia) {
      return null;
    }

    let pontos = 0;

    // Bilirrubina (mg/dL)
    if (bilirrubina < 2) pontos += 1;
    else if (bilirrubina <= 3) pontos += 2;
    else pontos += 3;

    // Albumina (g/dL)
    if (albumina > 3.5) pontos += 1;
    else if (albumina >= 2.8) pontos += 2;
    else pontos += 3;

    // INR
    if (inr < 1.7) pontos += 1;
    else if (inr <= 2.3) pontos += 2;
    else pontos += 3;

    // Ascite
    if (ascite === "ausente") pontos += 1;
    else if (ascite === "leve") pontos += 2;
    else if (ascite === "moderada") pontos += 3;

    // Encefalopatia
    if (encefalopatia === "ausente") pontos += 1;
    else if (encefalopatia === "grau1-2") pontos += 2;
    else if (encefalopatia === "grau3-4") pontos += 3;

    let classe = "A";
    if (pontos >= 7 && pontos <= 9) classe = "B";
    else if (pontos >= 10) classe = "C";

    return { score: pontos, classe };
  };

  // Gera lista de todas as chaves de inputs (será criada dinamicamente)
  const getAllInputKeys = (exameId: string) => {
    const keys: string[] = [];

    // Campos PRÉ-TX
    // Data sempre primeiro
    keys.push(`${exameId}-data`);
    if (!isPosTx && isFigado) {
      keys.push(`${exameId}-encefalopatia`, `${exameId}-ascite`);
    }

    // ✅ ADICIONAR CAMPOS PÓS-TX AQUI
    if (isPosTx) {
      keys.push(
        `${exameId}-temperatura`,
        `${exameId}-peso`,
        `${exameId}-dialise`, // ✅ ADICIONAR
        `${exameId}-transfusao`, // ✅ ADICIONAR
        `${exameId}-diurese`
      );
    }

    // Campos comuns (hemograma, bioquímica, etc)
    keys.push(
      `${exameId}-hemoglobina`,
      `${exameId}-hematocrito`,
      `${exameId}-leucocitos`,
      `${exameId}-neutrofilos`,
      `${exameId}-eosinofilos`,
      `${exameId}-linfocitos`,
      `${exameId}-bastonetes`,
      `${exameId}-plaquetas`,
      `${exameId}-tap`,
      `${exameId}-inr`,
      `${exameId}-ttpa`,
      `${exameId}-fibrinogenio`,
      `${exameId}-fatorV`,
      `${exameId}-bilirrubinaTotal`,
      `${exameId}-bilirrubinaDireta`,
      `${exameId}-bilirrubinaIndireta`,
      `${exameId}-tgo`,
      `${exameId}-tgp`,
      `${exameId}-fosfataseAlcalina`,
      `${exameId}-ggt`,
      `${exameId}-albumina`,
      `${exameId}-proteinasTotais`,
      `${exameId}-globulina`,
      `${exameId}-creatinina`,
      `${exameId}-ureia`,
      `${exameId}-sodio`,
      `${exameId}-potassio`,
      `${exameId}-magnesio`,
      `${exameId}-calcio`,
      `${exameId}-calcioIonico`,
      `${exameId}-fosforo`,
      `${exameId}-cloro`,
      `${exameId}-amilase`,
      `${exameId}-lipase`,
      `${exameId}-acidoUrico`,
      `${exameId}-ldh`,
      `${exameId}-cpk`,
      `${exameId}-ckMb`,
      `${exameId}-troponina`,
      `${exameId}-colesterolTotal`,
      `${exameId}-ldl`,
      `${exameId}-hdl`,
      `${exameId}-vldl`,
      `${exameId}-triglicerides`,
      // ✅ ADICIONAR METABÓLICOS:
      `${exameId}-glicemia`,
      `${exameId}-hemoglobinaGlicada`,
      `${exameId}-frutosamina`,
      `${exameId}-pcr`,
      `${exameId}-procalcitonina`,
      `${exameId}-lactato`,

      `${exameId}-tsh`,
      `${exameId}-t4Livre`,
      `${exameId}-t3Total`,

      // ✅ ADICIONAR GASOMETRIA:
      // Tipo é dropdown, pode pular
      `${exameId}-gasometriaTipo`, // ✅ ADICIONAR ESTA LINHA
      `${exameId}-gasometriaPh`,
      `${exameId}-gasometriaPco2`,
      `${exameId}-gasometriaPo2`,
      `${exameId}-gasometriaHco3`,
      `${exameId}-gasometriaBe`,
      `${exameId}-gasometriaSaturacao`
    );

    // Campos PÓS-TX (imunossupressão)
    if (isPosTx) {
      keys.push(
        `${exameId}-solumedrol`,
        `${exameId}-prednisona`,
        `${exameId}-tacrolimus`,
        `${exameId}-nivelTacrolimus`,
        `${exameId}-ciclosporina`,
        `${exameId}-nivelCiclosporina`,
        `${exameId}-mmf`,
        `${exameId}-mms`,
        `${exameId}-everolimus`,
        `${exameId}-nivelEverolimus`,
        `${exameId}-sirolimus`,
        `${exameId}-nivelSirolimus`,
        `${exameId}-azatioprina`,
        `${exameId}-thymoglobulina`
      );
    }

    return keys;
  };

  // ✅ ESTADO LOCAL SÓ PARA PÓS-TX:
  const [examesPosTxLocal, setExamesPosTxLocal] = useState<any[]>([]);

  // ✅ CARREGAR EXAMES DO CONTEXTO PARA O ESTADO LOCAL
  // E limpar linhas vazias editáveis que ficaram pendentes
  const camposExameCheck = [
    "hemoglobina",
    "hematocrito",
    "leucocitos",
    "neutrofilos",
    "eosinofilos",
    "linfocitos",
    "plaquetas",
    "tap",
    "inr",
    "ttpa",
    "fibrinogenio",
    "fatorV",
    "bilirrubinaTotal",
    "bilirrubinaDireta",
    "bilirrubinaIndireta",
    "tgo",
    "tgp",
    "fosfataseAlcalina",
    "ggt",
    "albumina",
    "creatinina",
    "ureia",
    "sodio",
    "potassio",
    "magnesio",
    "calcio",
    "colesterolTotal",
    "ldl",
    "hdl",
    "vldl",
    "triglicerides",
    "tsh",
    "t4Livre",
    "t3Total",
    "glicemia",
    "hemoglobinaGlicada",
    "acidoUrico",
    "amilase",
    "lipase",
    "calcioIonico",
    "fosforo",
    "cloro",
    "bastonetes",
    "ldh",
    "proteinasTotais",
    "globulina",
    "cpk",
    "ckMb",
    "troponina",
    "ferritina",
    "ferro",
    "saturacaoTransferrina",
    "afp",
    "cea",
    "ca199",
    "ca125",
    "tacrolimus",
    "ciclosporina",
    "everolimus",
  ];

  const exameTemDados = (exame: any) => {
    return camposExameCheck.some((campo) => {
      const valor = exame[campo];
      return (
        valor !== undefined && valor !== null && valor !== "" && valor !== 0
      );
    });
  };

  useEffect(() => {
    if (paciente && isPosTx) {
      const doContexto = listarExamesPosTx(paciente.id).filter(
        (e) => !e.tipoExame || e.tipoExame === "pos"
      );
      // AI_20: Sincronizar contexto → local APENAS quando estrutura muda
      // (exame adicionado, removido ou salvo). Não sobrescrever durante edição de células.
      setExamesPosTxLocal((prev) => {
        // AI_20: Comparar apenas IDs — mudanças de conteúdo ou editavel são geridas localmente
        const prevIds = prev
          .map((e) => e.id)
          .sort()
          .join(",");
        const newIds = doContexto
          .map((e) => e.id)
          .sort()
          .join(",");
        if (prevIds === newIds) {
          return prev; // Mesmos exames — preservar estado local (edições, editavel)
        }
        return doContexto; // Exame adicionado ou removido — ressincronizar
      });
    }
  }, [paciente?.id, isPosTx, listarExamesPosTx, refreshImport]);

  // Limpar linhas vazias PRÉ-TX ao carregar
  useEffect(() => {
    if (paciente && !isPosTx) {
      const lista = modoPreForcado
        ? listarExamesPreTx(paciente.id)
        : listarExamesPorPaciente(paciente.id);
      lista.forEach((e: any) => {
        if (e.editavel && !exameTemDados(e)) {
          removerExame(e.id).catch(() => {});
        }
      });
    }
  }, [paciente?.id, isPosTx]);

  // ✅ SE PÓS-TX: usa estado local, SE PRÉ-TX: usa contexto
  const exames = useMemo(() => {
    if (!paciente) return [];

    const lista = isPosTx
      ? examesPosTxLocal
      : modoPreForcado
      ? listarExamesPreTx(paciente.id)
      : listarExamesPorPaciente(paciente.id);

    // ✅ Separa editáveis e não-editáveis
    const editaveis = lista.filter((e) => e.editavel);
    const naoEditaveis = lista.filter((e) => !e.editavel);

    // ✅ Ordena APENAS os não-editáveis (salvos)
    const naoEditaveisOrdenados = [...naoEditaveis].sort((a, b) => {
      const dataComparacao =
        new Date(b.data).getTime() - new Date(a.data).getTime();
      if (dataComparacao !== 0) return dataComparacao;
      return b.id.localeCompare(a.id);
    });

    // ✅ Editáveis no topo, depois os ordenados
    return [...editaveis, ...naoEditaveisOrdenados];
  }, [paciente, isPosTx, modoPreForcado, examesPosTxLocal, listarExamesPorPaciente, listarExamesPreTx]);

  // Ref para garantir que só marca UMA VEZ quando confirma transplante
  const jaMarcoPreTxRef = useRef(false);

  useEffect(() => {
    const dataTx =
      paciente?.inscricaoTx?.dataTransplante || paciente?.dataTransplante;
    if (dataTx && !jaMarcoPreTxRef.current) {
      marcarExamesComoPreTx(paciente.id, dataTx);
      jaMarcoPreTxRef.current = true;
    }
  }, [
    paciente?.inscricaoTx?.dataTransplante,
    paciente?.dataTransplante,
    paciente?.id,
    marcarExamesComoPreTx,
  ]);

  // Reset quando trocar de paciente
  useEffect(() => {
    jaMarcoPreTxRef.current = false;
  }, [paciente?.id]);
  // 🎯 CALCULAR DIAS PÓS-OPERATÓRIO em formato: Xa Ym Zd
  const calcularPO = (dataExame: string, dataTransplante: string): string => {
    // Normalizar datas: converter DD/MM/AAAA → AAAA-MM-DD se necessário
    const normalizarData = (d: string): string => {
      if (!d) return d;
      if (d.includes("/")) {
        const partes = d.split("/");
        if (partes.length === 3)
          return `${partes[2]}-${partes[1]}-${partes[0]}`;
      }
      return d;
    };
    const examDate = new Date(normalizarData(dataExame) + "T12:00:00");
    const txDate = new Date(normalizarData(dataTransplante) + "T12:00:00");
    // Ajustar para fuso horário do Brasil (UTC-3)
    examDate.setHours(12, 0, 0, 0);
    txDate.setHours(12, 0, 0, 0);

    // Se data do exame for antes do transplante, não há PO a calcular.
    // B341 (AI_150): a mensagem antiga ("Data inválida") não dizia o que fazer —
    // no caso real a data tinha sido lida como 2006 em vez de 2026 e o médico
    // não tinha como saber o motivo. Agora aponta a causa e a ação.
    if (examDate < txDate) return "Data anterior ao Tx — corrija";

    // Calcular diferença
    let anos = examDate.getFullYear() - txDate.getFullYear();
    let meses = examDate.getMonth() - txDate.getMonth();
    let dias = examDate.getDate() - txDate.getDate();

    // Ajustar dias negativos
    if (dias < 0) {
      meses--;
      const ultimoDiaMesAnterior = new Date(
        examDate.getFullYear(),
        examDate.getMonth(),
        0
      ).getDate();
      dias += ultimoDiaMesAnterior;
    }

    // Ajustar meses negativos
    if (meses < 0) {
      anos--;
      meses += 12;
    }

    // Formatar saída
    const partes: string[] = [];
    if (anos > 0) partes.push(`${anos}a`);
    if (meses > 0) partes.push(`${meses}m`);
    if (dias > 0 || partes.length === 0) partes.push(`${dias}d`);

    return partes.join(" ");
  };

  const adicionarNovoExame = async () => {
    if (!paciente) return;

    // Verificar se já existe linha editável vazia
    const lista = isPosTx
      ? examesPosTxLocal
      : modoPreForcado
      ? listarExamesPreTx(paciente.id)
      : listarExamesPorPaciente(paciente.id);
    const linhaEditavelVazia = lista.find((e) => {
      if (!e.editavel) return false;
      return !exameTemDados(e);
    });

    if (linhaEditavelVazia) {
      alert("Preencha a linha de exame já criada antes de adicionar uma nova.");
      return;
    }

    try {
      if (isPosTx) {
        // AI_20: Adiciona no Firebase — onSnapshot atualiza contexto → useEffect sincroniza
        await adicionarExame(paciente.id, {
          data: getDataHoje(),
          editavel: true,
          tipoExame: "pos",
        });
      } else {
        // PRÉ-TX: Adiciona no contexto (modoPreForcado: marca explicitamente "pre"
        // num paciente já transplantado, p/ não vazar p/ o Folhão/pós).
        await adicionarExame(paciente.id, {
          data: getDataHoje(),
          editavel: true,
          tipoExame: modoPreForcado ? "pre" : undefined,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao adicionar exame:", error);
      alert("Erro ao adicionar exame. Tente novamente.");
    }
  };

  const salvarExame = (id: string) => {
    // Forçar blur ANTES da validação para garantir que valores sejam commitados
    const activeEl = document.activeElement as HTMLElement;
    if (activeEl && activeEl.tagName === "INPUT") {
      activeEl.blur();
    }
    // Pequeno delay para o React processar o blur antes de validar
    setTimeout(() => {
      const inputs = document.querySelectorAll(`[data-exame-id="${id}"] input`);
      let temValorDOM = false;
      inputs.forEach((input) => {
        const el = input as HTMLInputElement;
        // Ignorar: data, checkbox, e inputs de outras linhas
        if (el.type === "date") return;
        if (el.type === "checkbox") return;
        if (el.type === "text" && !el.value.trim()) return;
        const val = el.value.trim();
        if (val && val !== "" && val !== "0") {
          temValorDOM = true;
        }
      });
      // Fallback: verificar também no contexto
      const exame = isPosTx
        ? examesPosTxLocal.find((e) => e.id === id)
        : listarExamesPorPaciente(paciente.id).find((e: any) => e.id === id);
      if (!temValorDOM && (!exame || !exameTemDados(exame))) {
        // Remover linha vazia do Firebase e do estado local
        if (exame) {
          removerExame(id).catch(() => {});
          if (isPosTx) {
            setExamesPosTxLocal((prev) => prev.filter((e) => e.id !== id));
          }
        }
        alert("Nenhum exame digitado. A linha foi removida.");
        return;
      }
      // 📅 B296 — não finalizar exame com data futura (permite hoje/retroativo).
      if (exame && ehDataFutura((exame as any).data)) {
        alert(msgDataFutura("Data do exame"));
        return;
      }
      if (isPosTx) {
        editarExame(id, { editavel: false });
        setExamesPosTxLocal((prev) =>
          prev.map((e) => (e.id === id ? { ...e, editavel: false } : e))
        );
      } else {
        editarExame(id, { editavel: false });
      }
    }, 200);
  };

  const ativarEdicaoExame = (id: string) => {
    if (isPosTx) {
      setExamesPosTxLocal((prev) =>
        prev.map((e) => (e.id === id ? { ...e, editavel: true } : e))
      );
    } else {
      editarExame(id, { editavel: true });
    }
  };

  const removerExameLocal = async (id: string) => {
    if (window.confirm("Tem certeza que deseja remover este exame?")) {
      try {
        // ✅ Sempre remover do Firebase primeiro
        await removerExame(id);

        // ✅ Se for pós-TX, também remover do estado local
        if (isPosTx) {
          setExamesPosTxLocal((prev) => prev.filter((e) => e.id !== id));
        }
      } catch (error) {
        console.error("❌ Erro ao remover exame:", error);
        alert("Erro ao remover exame. Tente novamente.");
      }
    }
  };
  // Ref para dependências mutáveis do CelulaExame (evita recriação do componente)
  const celulaRef = useRef<any>({});
  celulaRef.current = {
    exames,
    editarExame,
    isPosTx,
    setExamesPosTxLocal,
    sexoFeminino,
    calcularChildPugh,
    getAllInputKeys,
    handleKeyDown,
    focusInputByKey,
  };

  // 📝 COMPONENTE DE INPUT — estabilizado com useMemo
  // React.memo inline recriava o TIPO do componente a cada render do pai
  // → React desmontava/remontava o input → foco perdido
  // useMemo([]) garante que o tipo é criado UMA VEZ
  const CelulaExame = useMemo(
    () =>
      React.memo(
        ({
          exameId,
          campo,
          valor,
          editavel,
          tipo = "number",
        }: {
          exameId: string;
          campo: keyof ExameLab;
          valor: any;
          editavel: boolean;
          tipo?: string;
        }) => {
          const validacao =
            tipo === "number" && campo in valoresNormais
              ? verificarValor(campo as keyof typeof valoresNormais, valor)
              : null;
          const handleSalvar = (novoValor: any) => {
            const deps = celulaRef.current;
            const exame = deps.exames.find((e: any) => e.id === exameId);
            if (!exame) return;
            const atualizado: any = { [campo]: novoValor };
            // Calcular MELD automaticamente
            const exameCompleto = { ...exame, ...atualizado };
            if (
              exameCompleto.creatinina &&
              exameCompleto.bilirrubinaTotal &&
              exameCompleto.inr &&
              exameCompleto.sodio &&
              exameCompleto.albumina
            ) {
              const resultado = calcularMELD({
                creatinina: exameCompleto.creatinina,
                bilirrubina: exameCompleto.bilirrubinaTotal,
                inr: exameCompleto.inr,
                sodio: exameCompleto.sodio,
                albumina: exameCompleto.albumina,
                sexoFeminino: deps.sexoFeminino,
              });
              atualizado.meld = resultado.score;
            } else if (COMPONENTES_MELD.has(campo)) {
              // 🐛 B338 (AI_149) — o médico apagou/invalidou um COMPONENTE do MELD:
              // o score gravado foi calculado com o valor antigo e agora está
              // OBSOLETO. Sem isto, apagar a albumina errada deixava para trás um
              // MELD inflado por ela (a tabela exibe o gravado quando não dá para
              // recalcular). Só limpa quando o campo editado É componente do MELD —
              // editar potássio nunca mexe no score.
              atualizado.meld = undefined;
            }
            // Calcular Child-Pugh automaticamente
            const childData = deps.calcularChildPugh(exameCompleto);
            if (childData) {
              atualizado.childPugh = `${childData.classe} (${childData.score}pts)`;
            } else if (COMPONENTES_CHILD.has(campo)) {
              atualizado.childPugh = undefined; // idem: derivado obsoleto (B338)
            }
            deps.editarExame(exameId, atualizado);
            if (deps.isPosTx) {
              deps.setExamesPosTxLocal((prev: any[]) =>
                prev.map((e) =>
                  e.id === exameId ? { ...e, ...atualizado } : e
                )
              );
            }
          };
          const inputKey = `${exameId}-${campo}`;
          const deps = celulaRef.current;
          const allKeys = deps.getAllInputKeys(exameId);
          return (
            <InputExame
              valor={valor}
              editavel={editavel}
              tipo={tipo}
              onSalvar={handleSalvar}
              validacao={validacao}
              inputRef={(el) => {
                inputRefs.current[inputKey] = el;
              }}
              onKeyDown={(e) => {
                deps.handleKeyDown(e, inputKey, allKeys, deps.focusInputByKey);
              }}
              dataKey={inputKey}
            />
          );
        }
      ),
    []
  );

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "16px",
      }}
    >
      <style>{`
        .tabela-exames-container::-webkit-scrollbar {
          width: 10px;
          height: 12px;
          display: block !important;
        }
        .tabela-exames-container::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 4px;
        }
        .tabela-exames-container::-webkit-scrollbar-thumb {
          background: #64748b;
          border-radius: 4px;
          min-height: 40px;
        }
        .tabela-exames-container::-webkit-scrollbar-thumb:hover {
          background: #475569;
        }
        .tabela-exames-container::-webkit-scrollbar-corner {
          background: #e2e8f0;
        }
        .tabela-exames-container {
          scrollbar-width: auto !important;
          scrollbar-color: #64748b #e2e8f0;
        }
      `}</style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme.gray[800],
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: theme.fonts.sans,
            margin: 0,
          }}
        >
          {isPosTx ? (
            <>
              <ClipboardList size={20} style={{ marginRight: "8px" }} /> Folhão
            </>
          ) : (
            <>
              <FlaskConical size={20} style={{ marginRight: "8px" }} /> Exames
              Laboratoriais
            </>
          )}
        </h2>

        {/* BOTÕES */}
        <div style={{ display: "flex", gap: "8px" }}>
          {/* Botão Analisar IA - só aparece se tiver exames */}
          {exames.length > 0 && podeGerarIA && (
            <button
              onClick={handleAnalisarFolhao}
              disabled={loadingAnalise}
              style={{
                background: loadingAnalise ? theme.gray[300] : "#8b5cf6",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontWeight: "600",
                cursor: loadingAnalise ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontFamily: theme.fonts.sans,
                transition: theme.transitions.normal,
                opacity: loadingAnalise ? 0.6 : 1,
              }}
              title="Analisar todos os exames com IA"
            >
              {loadingAnalise ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      animation: "spin 1s linear infinite",
                      fontSize: "16px",
                    }}
                  >
                    <Loader
                      size={16}
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  </span>
                  Analisando...
                </>
              ) : (
                <>
                  <Bot size={16} /> Analisar {isPosTx ? "Folhão" : "Exames"}
                </>
              )}
            </button>
          )}

          {/* 🧪 B323-FU (AI_145): reabre a última análise lab salva (durável).
              Sempre visível quando há análise salva (mesmo padrão das outras
              abas — sorologias/marcadores/evoluções). */}
          {!isConvidado && ultimaAnaliseLabDisponivel && (
              <button
                onClick={verUltimaAnaliseLab}
                style={{
                  background: "white",
                  color: "#8b5cf6",
                  border: "1px solid #8b5cf6",
                  padding: "8px 16px",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: theme.fonts.sans,
                  transition: theme.transitions.normal,
                }}
                title="Reabrir a última análise de IA gerada para estes exames"
              >
                <Bot size={16} /> Ver última análise
              </button>
            )}

          {/* B241: Importar exames de PDF — B243 (AI_110): visível a quem pode
              usar a IA de extração (médico/admin + enfermeiro/administrativo),
              espelhando api/_lib/autorizarEtapaIA.js. */}
          {podeImportarExamesPDF(usuarioLogado) && (
            <Button onClick={() => setMostrarImportar(true)}>
              <FileUp size={14} style={{ marginRight: "6px" }} /> Importar PDF
            </Button>
          )}

          {/* Botão Novo Exame */}
          {!isConvidado && (
            <Button onClick={adicionarNovoExame}>
              <Plus size={14} style={{ marginRight: "6px" }} /> Novo Exame
            </Button>
          )}
        </div>
      </div>

      {/* B241 (AI_108): modal de importação de exames laboratoriais por PDF */}
      {mostrarImportar && (
        <ImportarExamesLabModal
          paciente={paciente}
          isPosTx={isPosTx}
          onFechar={() => setMostrarImportar(false)}
          onImportado={() => setRefreshImport((n) => n + 1)}
        />
      )}

      {exames.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: "#f9fafb",
            borderRadius: theme.borderRadius.lg,
          }}
        >
          <FlaskConical
            size={60}
            color={theme.gray[300]}
            style={{ marginBottom: "16px" }}
          />

          <p
            style={{
              color: theme.gray[600],
              marginBottom: "16px",
              fontFamily: theme.fonts.sans,
            }}
          >
            Nenhum exame cadastrado ainda
          </p>
          {!isConvidado && (
            <Button onClick={adicionarNovoExame}>
              <Plus size={14} style={{ marginRight: "6px" }} /> Adicionar
              Primeiro Exame
            </Button>
          )}
        </div>
      ) : (
        <>
          <div
            className="tabela-exames-container"
            style={{
              maxHeight: "calc(100vh - 340px)",
              overflow: "scroll",
              border: `2px solid ${theme.gray[200]}`,
              borderRadius: theme.borderRadius.lg,
              position: "relative",
            }}
          >
            <table
              tabIndex={-1}
              style={{
                width: "max-content",
                minWidth: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: "13px",
                fontFamily: theme.fonts.sans,
                marginBottom: "60px",
              }}
            >
              <thead style={{ position: "sticky", top: 0, zIndex: 20 }}>
                <tr
                  style={{
                    background: theme.gradients.primary,
                    color: "white",
                  }}
                >
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      position: "sticky",
                      left: 0,
                      background: theme.primary[600],
                      zIndex: 30,
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Data
                  </th>
                  {isPosTx && (
                    <>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: theme.primary[600],
                          minWidth: "60px",
                          fontWeight: "600",
                        }}
                      >
                        PO
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ec4899",
                          minWidth: "55px",
                          fontWeight: "600",
                        }}
                      >
                        Temp (°C)
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ec4899",
                          minWidth: "55px",
                          fontWeight: "600",
                        }}
                      >
                        Peso (kg)
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ec4899",
                          minWidth: "55px",
                          fontWeight: "600",
                        }}
                      >
                        Diálise
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ec4899",
                          minWidth: "65px",
                          fontWeight: "600",
                        }}
                      >
                        Transfusão
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ec4899",
                          minWidth: "60px",
                          fontWeight: "600",
                        }}
                      >
                        Diurese (mL)
                      </th>
                    </>
                  )}
                  {!isPosTx && isFigado && (
                    <>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: theme.primary[700],
                          fontWeight: "bold",
                          minWidth: "55px",
                        }}
                      >
                        MELD 3.0
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          minWidth: "65px",
                          fontWeight: "600",
                        }}
                      >
                        Child-Pugh
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          minWidth: "70px",
                          fontWeight: "600",
                        }}
                      >
                        Encefalopatia
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          minWidth: "65px",
                          fontWeight: "600",
                        }}
                      >
                        Ascite
                      </th>
                    </>
                  )}
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Hemoglobina
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Hematócrito
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Leucócitos
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Neutrófilos
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Eosinófilos
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Linfócitos
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Bastonetes
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Plaquetas
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ef4444",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    TAP (%)
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ef4444",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    INR
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ef4444",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    TTPA
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ef4444",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Fibrinogênio
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ef4444",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    Fator V (%)
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Bili Total
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Bili Direta
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Bili Indireta
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    TGO
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    TGP
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Fosf. Alc.
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    GGT
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Albumina
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Prot. totais
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f59e0b",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Globulina
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#a855f7",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Creatinina
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#a855f7",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Ureia
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Sódio
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Potássio
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Magnésio
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Cálcio
                  </th>
                  {/* B241 fase 2 (AI_109) — novas colunas lab */}
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    Cálcio iônico
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    Fósforo
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Cloro
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Amilase
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Lipase
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    Ác. úrico
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    LDH
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    CPK
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    CK-MB
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#6366f1",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Troponina
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ec4899",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Col. Total
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ec4899",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    LDL
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ec4899",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    HDL
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ec4899",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    VLDL
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#ec4899",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Triglicerídeos
                  </th>
                  {/* ✅ METABÓLICOS / INFECÇÃO */}
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#8b5cf6",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    Glicemia
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#8b5cf6",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    HbA1c (%)
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#8b5cf6",
                      minWidth: "110px",
                      fontWeight: "600",
                    }}
                  >
                    Frutosamina
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#06b6d4",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    PCR
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#06b6d4",
                      minWidth: "70px",
                      fontWeight: "600",
                    }}
                  >
                    Procalcitonina
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#06b6d4",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    Lactato
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    TSH
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    T4 Livre
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#10b981",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    T3 total
                  </th>
                  {/* ✅ GASOMETRIA */}
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f97316",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Gaso Tipo
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f97316",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    pH
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f97316",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    PCO2
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f97316",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    PO2
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f97316",
                      minWidth: "60px",
                      fontWeight: "600",
                    }}
                  >
                    HCO3
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f97316",
                      minWidth: "55px",
                      fontWeight: "600",
                    }}
                  >
                    BE
                  </th>
                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      background: "#f97316",
                      minWidth: "65px",
                      fontWeight: "600",
                    }}
                  >
                    Sat O2 (%)
                  </th>

                  {isPosTx && (
                    <>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#f59e0b",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Solumedrol
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#f59e0b",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Prednisona
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#8b5cf6",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Dose TAC
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#8b5cf6",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Nível TAC
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#8b5cf6",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Dose CSA
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#8b5cf6",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Nível CSA
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#10b981",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        MMF
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#10b981",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        MMS
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ef4444",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Dose EVE
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ef4444",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Nível EVE
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ef4444",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Dose SIR
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#ef4444",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Nível SIR
                      </th>
                      {/* B241 (AI_108): colunas Dose RAPA / Nível RAPA removidas
                          (rapamicina = sirolimo). Ver colunas Dose SIR / Nível SIR. */}
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#6b7280",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Azatioprina
                      </th>
                      <th
                        style={{
                          border: `1px solid ${theme.primary[700]}`,
                          padding: "8px 4px",
                          background: "#6b7280",
                          color: "white",
                          fontWeight: "bold",
                          minWidth: "65px",
                        }}
                      >
                        Thymo
                      </th>
                    </>
                  )}

                  <th
                    style={{
                      border: `1px solid ${theme.primary[700]}`,
                      padding: "8px 4px",
                      position: "sticky",
                      right: 0,
                      background: theme.primary[600],
                      zIndex: 40,
                      boxShadow: "-4px 0 8px rgba(0,0,0,0.1)",
                      minWidth: "70px",
                      fontWeight: "600",
                    }}
                  >
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody>
                {exames.map((exame, index) => (
                  <tr
                    key={exame.id}
                    data-exame-id={exame.id}
                    style={{
                      background: index % 2 === 0 ? "white" : "#f9fafb",
                    }}
                  >
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "8px",
                        position: "sticky",
                        left: 0,
                        background: index % 2 === 0 ? "white" : "#f9fafb",
                        zIndex: 10,
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="data"
                        valor={exame.data}
                        editavel={exame.editavel}
                        tipo="date"
                      />
                    </td>

                    {/* ✅ CAMPOS PÓS-TX (logo após Data) */}
                    {isPosTx && (
                      <>
                        {/* PO - Dias pós-operatório (calculado automaticamente) */}
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                            background: index % 2 === 0 ? "white" : "#f9fafb",
                            fontFamily: theme.fonts.sans,
                            fontSize: "13px",
                            fontWeight: "600",
                            color: theme.primary[600],
                          }}
                        >
                          {(paciente?.inscricaoTx?.dataTransplante ||
                            paciente?.dataTransplante) &&
                          exame.data
                            ? `${calcularPO(
                                exame.data,
                                (paciente.inscricaoTx?.dataTransplante ||
                                  paciente.dataTransplante)!
                              )} `
                            : "-"}
                        </td>

                        {/* Temperatura */}
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="temperatura"
                            valor={exame.temperatura}
                            editavel={exame.editavel}
                            tipo="number"
                          />
                        </td>

                        {/* Peso */}
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="peso"
                            valor={exame.peso}
                            editavel={exame.editavel}
                            tipo="number"
                          />
                        </td>

                        {/* Diálise - Checkbox */}
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                            background: index % 2 === 0 ? "white" : "#f9fafb",
                          }}
                        >
                          {exame.editavel ? (
                            <input
                              ref={(el) => {
                                inputRefs.current[`${exame.id}-dialise`] = el;
                              }}
                              type="checkbox"
                              data-key={`${exame.id}-dialise`}
                              checked={exame.dialise || false}
                              onChange={(e) => {
                                const novoValor = e.target.checked;
                                editarExame(exame.id, { dialise: novoValor });

                                // ✅ ATUALIZAR ESTADO LOCAL PÓS-TX
                                if (isPosTx) {
                                  setExamesPosTxLocal((prev) =>
                                    prev.map((ex) =>
                                      ex.id === exame.id
                                        ? { ...ex, dialise: novoValor }
                                        : ex
                                    )
                                  );
                                }
                              }}
                              onKeyDown={(e) => {
                                const inputKey = `${exame.id}-dialise`;
                                const allKeys = getAllInputKeys(exame.id);
                                handleKeyDown(
                                  e,
                                  inputKey,
                                  allKeys,
                                  focusInputByKey
                                );
                              }}
                              style={{
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                fontFamily: theme.fonts.sans,
                                fontSize: "13px",
                                fontWeight: "600",
                              }}
                            >
                              {exame.dialise ? "Sim" : "-"}
                            </span>
                          )}
                        </td>

                        {/* Transfusão - Campo livre */}
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {exame.editavel ? (
                            <input
                              ref={(el) => {
                                inputRefs.current[`${exame.id}-transfusao`] =
                                  el;
                              }}
                              type="text"
                              data-key={`${exame.id}-transfusao`}
                              value={exame.transfusao || ""}
                              onChange={(e) => {
                                const novoValor = e.target.value;
                                editarExame(exame.id, {
                                  transfusao: novoValor,
                                });

                                // ✅ ATUALIZAR ESTADO LOCAL PÓS-TX
                                if (isPosTx) {
                                  setExamesPosTxLocal((prev) =>
                                    prev.map((ex) =>
                                      ex.id === exame.id
                                        ? { ...ex, transfusao: novoValor }
                                        : ex
                                    )
                                  );
                                }
                              }}
                              onKeyDown={(e) => {
                                const inputKey = `${exame.id}-transfusao`;
                                const allKeys = getAllInputKeys(exame.id);
                                handleKeyDown(
                                  e,
                                  inputKey,
                                  allKeys,
                                  focusInputByKey
                                );
                              }}
                              style={{
                                width: "90%",
                                padding: "4px 6px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                fontSize: "13px",
                                fontFamily: theme.fonts.sans,
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                fontFamily: theme.fonts.sans,
                                fontSize: "13px",
                              }}
                            >
                              {exame.transfusao || "-"}
                            </span>
                          )}
                        </td>

                        {/* Diurese */}
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="diurese"
                            valor={exame.diurese}
                            editavel={exame.editavel}
                            tipo="number"
                          />
                        </td>
                      </>
                    )}

                    {!isPosTx && isFigado && (
                      <>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "16px",
                            background: "#dbeafe",
                          }}
                        >
                          {(() => {
                            // B248 (AI_111): MELD recalculado AO VIVO (igual ao
                            // Child-Pugh ao lado), não dependendo do valor gravado.
                            // Assim aparece mesmo em exames importados/antigos sem
                            // meld salvo, sem precisar regravar.
                            if (
                              exame.creatinina &&
                              exame.bilirrubinaTotal &&
                              exame.inr &&
                              exame.sodio &&
                              exame.albumina
                            ) {
                              return calcularMELD({
                                creatinina: Number(exame.creatinina),
                                bilirrubina: Number(exame.bilirrubinaTotal),
                                inr: Number(exame.inr),
                                sodio: Number(exame.sodio),
                                albumina: Number(exame.albumina),
                                sexoFeminino,
                              }).score;
                            }
                            return exame.meld || "-";
                          })()}
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "4px",
                            textAlign: "center",
                          }}
                        >
                          {(() => {
                            const child = calcularChildPugh(exame);
                            if (child) {
                              const corClasse =
                                child.classe === "A"
                                  ? "#10b981"
                                  : child.classe === "B"
                                  ? "#f59e0b"
                                  : "#ef4444";
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontFamily: theme.fonts.sans,
                                      fontSize: "14px",
                                      fontWeight: "700",
                                      color: corClasse,
                                    }}
                                  >
                                    {child.classe} ({child.score}pts)
                                  </span>
                                </div>
                              );
                            } else {
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontFamily: theme.fonts.sans,
                                      fontSize: "12px",
                                      color: theme.gray[400],
                                    }}
                                  >
                                    -
                                  </span>
                                </div>
                              );
                            }
                          })()}
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {exame.editavel ? (
                            <select
                              ref={(el) => {
                                inputRefs.current[`${exame.id}-encefalopatia`] =
                                  el;
                              }}
                              value={exame.encefalopatia || ""}
                              data-key={`${exame.id}-encefalopatia`}
                              onChange={(e) => {
                                const novoValor = e.target.value;
                                const valorTipado =
                                  novoValor === "" ? undefined : novoValor;
                                const exameAtualizado = {
                                  ...exame,
                                  encefalopatia: valorTipado,
                                };
                                const childData =
                                  calcularChildPugh(exameAtualizado);
                                editarExame(exame.id, {
                                  encefalopatia: valorTipado,
                                  ...(childData
                                    ? {
                                        childPugh: `${childData.classe} (${childData.score}pts)`,
                                      }
                                    : {}),
                                });
                              }}
                              onKeyDown={(e) => {
                                const inputKey = `${exame.id}-encefalopatia`;
                                const allKeys = getAllInputKeys(exame.id);
                                handleKeyDown(
                                  e,
                                  inputKey,
                                  allKeys,
                                  focusInputByKey
                                );
                              }}
                              style={{
                                width: "90%",
                                padding: "4px 6px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                fontSize: "13px",
                                fontFamily: theme.fonts.sans,
                              }}
                            >
                              <option value="">-</option>
                              <option value="ausente">Ausente</option>
                              <option value="grau1-2">Grau 1-2</option>
                              <option value="grau3-4">Grau 3-4</option>
                            </select>
                          ) : (
                            <span
                              style={{
                                fontFamily: theme.fonts.sans,
                                fontSize: "13px",
                              }}
                            >
                              {exame.encefalopatia === "ausente"
                                ? "Ausente"
                                : exame.encefalopatia === "grau1-2"
                                ? "Grau 1-2"
                                : exame.encefalopatia === "grau3-4"
                                ? "Grau 3-4"
                                : "-"}
                            </span>
                          )}
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {exame.editavel ? (
                            <select
                              ref={(el) => {
                                inputRefs.current[`${exame.id}-ascite`] = el;
                              }}
                              value={exame.ascite || ""}
                              data-key={`${exame.id}-ascite`}
                              onChange={(e) => {
                                const novoValor = e.target.value;
                                const valorTipado =
                                  novoValor === "" ? undefined : novoValor;
                                const exameAtualizado = {
                                  ...exame,
                                  ascite: valorTipado,
                                };
                                const childData =
                                  calcularChildPugh(exameAtualizado);
                                editarExame(exame.id, {
                                  ascite: valorTipado,
                                  ...(childData
                                    ? {
                                        childPugh: `${childData.classe} (${childData.score}pts)`,
                                      }
                                    : {}),
                                });
                              }}
                              onKeyDown={(e) => {
                                const inputKey = `${exame.id}-ascite`;
                                const allKeys = getAllInputKeys(exame.id);
                                handleKeyDown(
                                  e,
                                  inputKey,
                                  allKeys,
                                  focusInputByKey
                                );
                              }}
                              style={{
                                width: "90%",
                                padding: "4px 6px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                fontSize: "13px",
                                fontFamily: theme.fonts.sans,
                              }}
                            >
                              <option value="">-</option>
                              <option value="ausente">Ausente</option>
                              <option value="leve">Leve/Controlada</option>
                              <option value="moderada">Moderada/Tensa</option>
                            </select>
                          ) : (
                            <span
                              style={{
                                fontFamily: theme.fonts.sans,
                                fontSize: "13px",
                              }}
                            >
                              {exame.ascite === "ausente"
                                ? "Ausente"
                                : exame.ascite === "leve"
                                ? "Leve"
                                : exame.ascite === "moderada"
                                ? "Moderada"
                                : "-"}
                            </span>
                          )}
                        </td>
                      </>
                    )}

                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="hemoglobina"
                        valor={exame.hemoglobina}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="hematocrito"
                        valor={exame.hematocrito}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="leucocitos"
                        valor={exame.leucocitos}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="neutrofilos"
                        valor={exame.neutrofilos}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="eosinofilos"
                        valor={exame.eosinofilos}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="linfocitos"
                        valor={exame.linfocitos}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="bastonetes" valor={exame.bastonetes} editavel={exame.editavel} />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="plaquetas"
                        valor={exame.plaquetas}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="tap"
                        valor={exame.tap}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="inr"
                        valor={exame.inr}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="ttpa"
                        valor={exame.ttpa}
                        editavel={exame.editavel}
                      />
                    </td>
                    {/* Fibrinogênio */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="fibrinogenio"
                        valor={exame.fibrinogenio}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* Fator V */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="fatorV"
                        valor={exame.fatorV}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="bilirrubinaTotal"
                        valor={exame.bilirrubinaTotal}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="bilirrubinaDireta"
                        valor={exame.bilirrubinaDireta}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="bilirrubinaIndireta"
                        valor={exame.bilirrubinaIndireta}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="tgo"
                        valor={exame.tgo}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="tgp"
                        valor={exame.tgp}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="fosfataseAlcalina"
                        valor={exame.fosfataseAlcalina}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="ggt"
                        valor={exame.ggt}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="albumina"
                        valor={exame.albumina}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="proteinasTotais" valor={exame.proteinasTotais} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="globulina" valor={exame.globulina} editavel={exame.editavel} />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="creatinina"
                        valor={exame.creatinina}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="ureia"
                        valor={exame.ureia}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="sodio"
                        valor={exame.sodio}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="potassio"
                        valor={exame.potassio}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="magnesio"
                        valor={exame.magnesio}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="calcio"
                        valor={exame.calcio}
                        editavel={exame.editavel}
                      />
                    </td>
                    {/* B241 fase 2 (AI_109) — novas células lab (mesma ordem do cabeçalho) */}
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="calcioIonico" valor={exame.calcioIonico} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="fosforo" valor={exame.fosforo} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="cloro" valor={exame.cloro} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="amilase" valor={exame.amilase} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="lipase" valor={exame.lipase} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="acidoUrico" valor={exame.acidoUrico} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="ldh" valor={exame.ldh} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="cpk" valor={exame.cpk} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="ckMb" valor={exame.ckMb} editavel={exame.editavel} />
                    </td>
                    <td tabIndex={-1} style={{ border: `1px solid ${theme.gray[300]}`, padding: "4px", textAlign: "center" }}>
                      <CelulaExame exameId={exame.id} campo="troponina" valor={exame.troponina} editavel={exame.editavel} />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="colesterolTotal"
                        valor={exame.colesterolTotal}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="ldl"
                        valor={exame.ldl}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="hdl"
                        valor={exame.hdl}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="vldl"
                        valor={exame.vldl}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="triglicerides"
                        valor={exame.triglicerides}
                        editavel={exame.editavel}
                      />
                    </td>
                    {/* ✅ METABÓLICOS / INFECÇÃO */}
                    {/* Glicemia */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="glicemia"
                        valor={exame.glicemia}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* Hemoglobina Glicada */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="hemoglobinaGlicada"
                        valor={exame.hemoglobinaGlicada}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* Frutosamina */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="frutosamina"
                        valor={exame.frutosamina}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* PCR */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="pcr"
                        valor={exame.pcr}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* Procalcitonina */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="procalcitonina"
                        valor={exame.procalcitonina}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* Lactato */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="lactato"
                        valor={exame.lactato}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="tsh"
                        valor={exame.tsh}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="t4Livre"
                        valor={exame.t4Livre}
                        editavel={exame.editavel}
                      />
                    </td>
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="t3Total"
                        valor={exame.t3Total}
                        editavel={exame.editavel}
                      />
                    </td>
                    {/* ✅ GASOMETRIA */}
                    {/* Tipo - Dropdown */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      {exame.editavel ? (
                        <select
                          ref={(el) => {
                            inputRefs.current[`${exame.id}-gasometriaTipo`] =
                              el;
                          }}
                          data-key={`${exame.id}-gasometriaTipo`}
                          value={exame.gasometriaTipo || ""}
                          onChange={(e) => {
                            const novoValor = e.target.value;
                            const valorTipado =
                              novoValor === ""
                                ? undefined
                                : (novoValor as "arterial" | "venosa");

                            editarExame(exame.id, {
                              gasometriaTipo: valorTipado,
                            });

                            // ✅ ATUALIZAR ESTADO LOCAL PÓS-TX
                            if (isPosTx) {
                              setExamesPosTxLocal((prev) =>
                                prev.map((ex) =>
                                  ex.id === exame.id
                                    ? { ...ex, gasometriaTipo: valorTipado }
                                    : ex
                                )
                              );
                            }
                          }}
                          onKeyDown={(e) => {
                            const inputKey = `${exame.id}-gasometriaTipo`;
                            const allKeys = getAllInputKeys(exame.id);
                            handleKeyDown(
                              e,
                              inputKey,
                              allKeys,
                              focusInputByKey
                            );
                          }}
                          style={{
                            width: "90%",
                            padding: "4px 6px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <option value="">-</option>
                          <option value="arterial">Arterial</option>
                          <option value="venosa">Venosa</option>
                        </select>
                      ) : (
                        <span
                          style={{
                            fontFamily: theme.fonts.sans,
                            fontSize: "13px",
                          }}
                        >
                          {exame.gasometriaTipo === "arterial"
                            ? "Arterial"
                            : exame.gasometriaTipo === "venosa"
                            ? "Venosa"
                            : "-"}
                        </span>
                      )}
                    </td>

                    {/* pH */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="gasometriaPh"
                        valor={exame.gasometriaPh}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* PCO2 */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="gasometriaPco2"
                        valor={exame.gasometriaPco2}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* PO2 */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="gasometriaPo2"
                        valor={exame.gasometriaPo2}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* HCO3 */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="gasometriaHco3"
                        valor={exame.gasometriaHco3}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* BE */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="gasometriaBe"
                        valor={exame.gasometriaBe}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {/* Saturação O2 */}
                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <CelulaExame
                        exameId={exame.id}
                        campo="gasometriaSaturacao"
                        valor={exame.gasometriaSaturacao}
                        editavel={exame.editavel}
                        tipo="number"
                      />
                    </td>

                    {isPosTx && (
                      <>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#fef3c7",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="solumedrol"
                            valor={exame.solumedrol}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#fef3c7",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="prednisona"
                            valor={exame.prednisona}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#ede9fe",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="tacrolimus"
                            valor={exame.tacrolimus}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#ede9fe",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="nivelTacrolimus"
                            valor={exame.nivelTacrolimus}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#ede9fe",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="ciclosporina"
                            valor={exame.ciclosporina}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#ede9fe",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="nivelCiclosporina"
                            valor={exame.nivelCiclosporina}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#d1fae5",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="mmf"
                            valor={exame.mmf}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#d1fae5",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="mms"
                            valor={exame.mms}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#fee2e2",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="everolimus"
                            valor={exame.everolimus}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#fee2e2",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="nivelEverolimus"
                            valor={exame.nivelEverolimus}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#fee2e2",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="sirolimus"
                            valor={exame.sirolimus}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#fee2e2",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="nivelSirolimus"
                            valor={exame.nivelSirolimus}
                            editavel={exame.editavel}
                          />
                        </td>
                        {/* B241 (AI_108): células rapamicina/nivelRapamicina
                            removidas (= sirolimo). Alinhado com a remoção dos
                            cabeçalhos Dose RAPA / Nível RAPA. */}
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#e5e7eb",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="azatioprina"
                            valor={exame.azatioprina}
                            editavel={exame.editavel}
                          />
                        </td>
                        <td
                          tabIndex={-1}
                          style={{
                            border: `1px solid ${theme.gray[300]}`,
                            padding: "8px",
                            background: "#e5e7eb",
                          }}
                        >
                          <CelulaExame
                            exameId={exame.id}
                            campo="thymoglobulina"
                            valor={exame.thymoglobulina}
                            editavel={exame.editavel}
                          />
                        </td>
                      </>
                    )}

                    <td
                      tabIndex={-1}
                      style={{
                        border: `1px solid ${theme.gray[300]}`,
                        padding: "8px",
                        position: "sticky",
                        right: 0,
                        background: index % 2 === 0 ? "white" : "#f9fafb",
                        zIndex: 10,
                        boxShadow: "-4px 0 8px rgba(0,0,0,0.08)",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          justifyContent: "center",
                        }}
                      >
                        {!isConvidado &&
                          (() => {
                            const dataCriacao = exame.criadoEm
                              ? new Date(exame.criadoEm)
                              : null;
                            const agora = new Date();
                            const horasDecorridas = dataCriacao
                              ? (agora.getTime() - dataCriacao.getTime()) /
                                (1000 * 60 * 60)
                              : 999;
                            // B323 (AI_145): equipe edita/exclui só rascunho ou
                            // dentro de 24h (< 24, alinhado à regra do Firestore
                            // que nega no instante exato de 24h). Admin passa
                            // sempre (escape B296). Espelha a fechadura das rules.
                            const podeEditarExcluir =
                              exame.editavel || horasDecorridas < 24 || ehAdmin;

                            return podeEditarExcluir ? (
                              <>
                                {exame.editavel ? (
                                  <button
                                    onClick={() => salvarExame(exame.id)}
                                    style={{
                                      background: theme.gradients.primary,
                                      color: "white",
                                      border: "none",
                                      padding: "6px 12px",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      transition: theme.transitions.normal,
                                    }}
                                    title="Salvar exame"
                                  >
                                    Salvar
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => ativarEdicaoExame(exame.id)}
                                    style={{
                                      background: "#f59e0b",
                                      color: "white",
                                      border: "none",
                                      padding: "6px 8px",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      transition: theme.transitions.normal,
                                    }}
                                    title="Editar exame"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => removerExameLocal(exame.id)}
                                  style={{
                                    color: "#ef4444",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    transition: theme.transitions.normal,
                                  }}
                                  title="Remover exame"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            ) : null;
                          })()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            style={{
              marginTop: "16px",
              padding: "16px",
              background: "#dbeafe",
              borderRadius: theme.borderRadius.lg,
              fontSize: "13px",
            }}
          >
            <p
              style={{
                fontWeight: "600",
                color: "#1e40af",
                marginBottom: "8px",
                fontFamily: theme.fonts.sans,
              }}
            >
              ℹ️ Instruções:
            </p>
            <ul
              style={{
                color: "#1e3a8a",
                margin: 0,
                paddingLeft: "20px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <li>Digite os valores - são salvos automaticamente após 500ms</li>
              <li>
                Valores{" "}
                <span style={{ color: "#ef4444", fontWeight: "bold" }}>
                  em vermelho
                </span>{" "}
                estão fora do normal (↑ ↓)
              </li>
              <li>
                Valores{" "}
                <span style={{ color: "#10b981", fontWeight: "bold" }}>
                  em verde
                </span>{" "}
                estão normais (✓)
              </li>
              <li>
                O <strong>MELD 3.0</strong> é calculado automaticamente
              </li>
              <li>
                Use{" "}
                <strong>
                  <Pencil size={12} style={{ marginRight: "4px" }} /> Editar
                </strong>{" "}
                para modificar um exame salvo
              </li>
            </ul>
          </div>

          {mostrarAnalise && analiseAtual && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
                padding: "20px",
              }}
            >
              {/* B313/AI_141 — NÃO fecha ao clicar fora (só no X/Fechar);
                  design alinhado ao de marcadores/imagem (cabeçalho roxo). */}
              <div
                id="analise-ia-folhao"
                style={{
                  background: "white",
                  borderRadius: theme.borderRadius.xl,
                  width: "100%",
                  maxWidth: "900px",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                }}
              >
                {/* HEADER FIXO */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "20px 24px",
                    flexShrink: 0,
                    background:
                      "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                    color: "white",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "white",
                      margin: 0,
                      fontFamily: theme.fonts.sans,
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    🧪 Análise Inteligente dos Exames
                  </h3>
                  <button
                    onClick={() => setMostrarAnalise(false)}
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      border: "none",
                      color: "white",
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      fontSize: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Fechar análise"
                  >
                    ✕
                  </button>
                </div>

                {/* CONTEÚDO SCROLLÁVEL */}
                <div
                  style={{
                    padding: "20px",
                    paddingBottom: "24px",
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {/* STATUS GERAL (B275: Gravidade × Urgência) */}
                  {analiseAtual.resumo?.statusGeral && (
                    <div style={{ marginBottom: "16px" }}>
                      <SelosStatus
                        gravidade={analiseAtual.resumo.gravidade}
                        urgencia={analiseAtual.resumo.urgencia}
                        statusGeral={analiseAtual.resumo.statusGeral}
                      />
                    </div>
                  )}

                  {/* PRINCIPAIS ACHADOS */}
                  {analiseAtual.resumo?.principaisAchados &&
                    analiseAtual.resumo.principaisAchados.length > 0 && (
                      <div style={{ marginBottom: "20px" }}>
                        <h4
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color: theme.gray[700],
                            marginBottom: "10px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <ClipboardList
                            size={14}
                            style={{ marginRight: "6px" }}
                          />{" "}
                          PRINCIPAIS ACHADOS:
                        </h4>
                        {/* B244 (AI_110): renderiza o markdown da análise
                            (antes saía cru, como "parede de texto"). */}
                        <div>
                          {analiseAtual.resumo.principaisAchados.map(
                            (achado, idx) => (
                              <TextoAnaliseMarkdown key={idx} texto={achado} />
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* ALERTAS CRÍTICOS */}
                  {analiseAtual.resumo?.alertasCriticos &&
                    analiseAtual.resumo.alertasCriticos.length > 0 && (
                      <div style={{ marginBottom: "20px" }}>
                        <h4
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#dc2626",
                            marginBottom: "10px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <AlertTriangle
                            size={14}
                            style={{ marginRight: "6px" }}
                          />{" "}
                          ALERTAS CRÍTICOS:
                        </h4>
                        {analiseAtual.resumo.alertasCriticos.map(
                          (alerta, idx) => (
                            <div
                              key={idx}
                              style={{
                                background: "#fee2e2",
                                border: "1px solid #fca5a5",
                                borderRadius: theme.borderRadius.md,
                                padding: "12px 14px",
                                marginBottom: "10px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "#991b1b",
                                  marginBottom: "6px",
                                  fontSize: "15px",
                                  fontFamily: theme.fonts.sans,
                                }}
                              >
                                {alerta.titulo}
                              </div>
                              <div
                                style={{
                                  color: "#7f1d1d",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                  marginBottom: "6px",
                                  fontFamily: theme.fonts.sans,
                                }}
                              >
                                {alerta.descricao}
                              </div>
                              {alerta.valorAtual && (
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "#991b1b",
                                    fontWeight: "600",
                                    fontFamily: theme.fonts.mono,
                                  }}
                                >
                                  Valor: {alerta.valorAtual}
                                  {alerta.valorReferencia &&
                                    ` (Referência: ${alerta.valorReferencia})`}
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    )}

                  {/* SUGESTÕES DE CONDUTA */}
                  {analiseAtual.sugestoes &&
                    analiseAtual.sugestoes.length > 0 && (
                      <div>
                        <h4
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color: theme.gray[700],
                            marginBottom: "10px",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <Lightbulb size={14} style={{ marginRight: "6px" }} />{" "}
                          SUGESTÕES DE CONDUTA:
                        </h4>
                        {analiseAtual.sugestoes.map((sugestao, idx) => (
                          <div
                            key={idx}
                            style={{
                              background: "white",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.md,
                              padding: "12px 14px",
                              marginBottom: "10px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: "6px",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: theme.gray[800],
                                  fontSize: "15px",
                                  flex: 1,
                                  fontFamily: theme.fonts.sans,
                                }}
                              >
                                {sugestao.titulo}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  padding: "2px 8px",
                                  borderRadius: "4px",
                                  background:
                                    sugestao.prioridade === "alta"
                                      ? "#fee2e2"
                                      : sugestao.prioridade === "media"
                                      ? "#fef3c7"
                                      : "#e0e7ff",
                                  color:
                                    sugestao.prioridade === "alta"
                                      ? "#dc2626"
                                      : sugestao.prioridade === "media"
                                      ? "#d97706"
                                      : "#4f46e5",
                                  fontFamily: theme.fonts.sans,
                                }}
                              >
                                {sugestao.prioridade === "alta"
                                  ? "ALTA"
                                  : sugestao.prioridade === "media"
                                  ? "MÉDIA"
                                  : "BAIXA"}
                              </div>
                            </div>
                            <div
                              style={{
                                color: theme.gray[600],
                                fontSize: "14px",
                                lineHeight: "1.5",
                                marginBottom: "6px",
                                fontFamily: theme.fonts.sans,
                              }}
                            >
                              {sugestao.conduta}
                            </div>
                            {sugestao.justificativa && (
                              <div
                                style={{
                                  color: theme.gray[500],
                                  fontSize: "13px",
                                  lineHeight: "1.4",
                                  marginBottom: "6px",
                                  fontFamily: theme.fonts.sans,
                                  fontStyle: "italic",
                                }}
                              >
                                💭 {sugestao.justificativa}
                              </div>
                            )}
                            {sugestao.prazo && (
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: theme.gray[500],
                                  fontFamily: theme.fonts.sans,
                                }}
                              >
                                <Clock
                                  size={12}
                                  style={{ marginRight: "4px" }}
                                />{" "}
                                {sugestao.prazo}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
